import type { Tier } from '../tier';

/**
 * ── ⭐ THE FOG FOLD (F1) — ONE RULE, N ADAPTERS, CHOSEN ONCE AT NODE-BUILD TIME ─────────────────
 *
 * The owner asked for *"a generic volumetric fog component, similar to UE's, that when added,
 * would work with the level… we'd need our rendering perf tiers… and have a mobile fallback so
 * that we could dial it in separately"*. That arrives feature-shaped. The system-shaped statement
 * is the one `csmRig.composeDistantShadow` and `indirect.ts` already make in this repo:
 *
 *   > **Fog is a TERM applied to the frame after shading. It has ONE authored config, ONE writer
 *   > (`scene.applyFog`), and N IMPLEMENTATIONS — and which implementation runs is a fold over
 *   > (config × tier), never a second set of authored numbers.**
 *
 * So this module is not "the volumetric fog subsystem". It is the table that subsystem will be a
 * ROW IN. `resolveFogPlan` is the one fold; the runtime node, the Details panel and
 * `__fwg.state().fog` all read IT, so the panel cannot describe a fog the frame is not running.
 *
 * ── WHY IT IMPORTS ONLY `type Tier` ─────────────────────────────────────────────────────────────
 *
 * `lightModel.ts` imports NOTHING, and its header explains why (it is on `bake-world.ts`'s
 * unbundled `node --experimental-strip-types` chain). This file is not on that chain, but it takes
 * the same discipline for the reason `probeCompile.ts` does: **a wrong plan does not fail.** A fog
 * that silently ran `linear` where the artist authored `height` renders, reports success and looks
 * like a taste difference. Pure means `npm run check` can hold the whole (mode × tier) matrix with
 * no GPU, no browser and no bake. `type Tier` is erased at compile time and `tier.ts` itself
 * imports nothing.
 *
 * ── ⚠️ WHAT THIS TABLE MAY DO, AND WHAT IT MAY NOT ─────────────────────────────────────────────
 *
 * It may SELECT an implementation and CAP or SCALE an authored number — the owner's own words for
 * the shadow-resolution lever ("a resolution cap or multiplier … tied to general perf settings").
 * It may **never author a number the artist did not**: a per-tier set of authored densities would
 * be exactly the "two competing values/systems" he objected to in the shadow panel.
 *
 * ⚠️ AND IT DOES NOT INVENT A FAKE DOWNGRADE TO LOOK COMPLETE. At F1 every tier runs the SAME
 * implementation, because a height fog is ~6 ALU with ZERO texture fetches and there is nothing to
 * fall back FROM; a tier-0 density cap would be a number no artist authored and no measurement
 * justified. What the tier argument really decides today is the SENTENCE — tier 0 is the WebGL2
 * fallback backend, which can never run the froxel volumetric at all (no compute, no 3D storage
 * textures), and that is a capability fact rather than a tuning preference. `notes` therefore
 * differ per tier today and `impl` does not, and both facts are asserted in
 * `tests/renderFogModel.test.ts` rather than claimed here.
 */

/** What the frame actually runs. `off` is a real arm: `scene.fog` AND `scene.fogNode` both null. */
export type FogImpl = 'off' | 'linear' | 'height';

/**
 * What the world AUTHORS. ⛔ `volumetric` IS DELIBERATELY ABSENT until an implementation exists —
 * the same ruling `FADE_DISTANCE_REFUSAL` made for lights: the enum grows WITH its implementation
 * or not at all. A mode an artist can pick that renders as something else is this repo's most
 * logged failure shape (nineteen instances), and it would be worse here than usual because fog is
 * a look, so "it did nothing" and "I did not like it" are indistinguishable on screen.
 */
export type FogMode = 'linear' | 'height';

/**
 * The authored block, STRUCTURALLY (`WorldFogSchema`'s output shape). Declared rather than imported
 * for the purity reason above — `world/schema.ts` pulls in zod and `probeBrick.ts`. The schema is
 * still the single source of the DEFAULTS; this is only its shape.
 */
export interface FogConfigInput {
  readonly enabled: boolean;
  readonly color: string;
  readonly nearM: number;
  readonly farM: number;
  readonly mode: FogMode;
  readonly density: number;
  readonly topM: number;
}

export interface FogTierRow {
  /**
   * The implementations this tier's BACKEND can run, in preference order. A capability list, not a
   * taste list: tier 0 is the WebGL2 fallback (`render/tier.ts`), and the froxel volumetric needs a
   * compute pass and a 3D storage texture, neither of which exists on that backend at any budget.
   * When `volumetric` is built it joins tier 2's list and NOT tier 0's, and this fold downgrades
   * with a note instead of the frame silently disagreeing with the panel.
   */
  readonly impls: readonly FogImpl[];
  /** The sentence the panel shows for this tier. Always true, always present — see the header. */
  readonly note: string;
}

/**
 * ⚠️ THE THREE ROWS ARE IDENTICAL IN `impls` TODAY AND THAT IS THE HONEST STATE, NOT AN OVERSIGHT.
 * The height fog is ALU-only; there is no measured reason to run a different one anywhere. The
 * rows exist so the day a second implementation lands it is a CELL EDIT rather than a new
 * authority — which is the whole reason this file is being written before that implementation.
 */
export const FOG_TIER_ROWS: Readonly<Record<Tier, FogTierRow>> = {
  0: {
    impls: ['linear', 'height'],
    note:
      'tier 0 is the WebGL2 fallback backend: no compute pass and no 3D storage textures, so the ' +
      'froxel volumetric can NEVER run on this tier. Height fog is the ceiling here by capability, ' +
      'not by tuning.',
  },
  1: {
    impls: ['linear', 'height'],
    note:
      'tier 1 is mobile WebGPU. No tablet has been measured for this term; height fog is ALU-only ' +
      '(zero texture fetches) which is why it ships here unchanged rather than dialled down.',
  },
  2: {
    impls: ['linear', 'height'],
    note:
      'tier 2 runs the authored mode as written. The volumetric implementation is NOT built: it ' +
      'is refused pending a verified raymarch/compute TSL pattern and a measured cost (design C).',
  },
};

export interface FogPlan {
  readonly tier: Tier;
  /** What the world asked for — carried even when fog is off, so the panel can still show it. */
  readonly authoredMode: FogMode;
  /** What the frame runs. `impl !== authoredMode` ⇒ `downgraded` and a note saying why. */
  readonly impl: FogImpl;
  readonly downgraded: boolean;
  readonly color: string;
  /** Linear-mode uniforms. Carried whatever the mode, so a mode flip does not lose the numbers. */
  readonly nearM: number;
  readonly farM: number;
  /** Height-mode uniforms. See `HEIGHT_DENSITY_NOTE` for the unit — it is NOT per metre. */
  readonly density: number;
  readonly topM: number;
  readonly notes: readonly string[];
}

/**
 * ⚠️ THE UNIT, SPELLED OUT, BECAUSE THE FIELD NAME CANNOT CARRY IT AND A WRONG GUESS IS A DEAD
 * SLIDER. three's `exponentialHeightFogFactor(density, height)` computes
 *
 *     distance = max(height - positionWorld.y, 0)      // metres BELOW the fog top
 *     m        = distance * viewZ                      // metres squared
 *     factor   = 1 - exp(-(density * m)^2)
 *
 * so `density` is in **1/m²**, and the number an artist reasons about is *where does this saturate*.
 * Worked: at ground level under a 40 m top, viewZ = 100 m ⇒ m = 4000 m², and `density = 2.5e-4`
 * puts `density * m = 1`, i.e. factor = 1 − 1/e ≈ 0.63 at 100 m. That is why the schema default is
 * 0.00025 and why the widget label says "1/m^2" rather than "per m".
 */
export const HEIGHT_DENSITY_NOTE =
  'Height fog density is in 1/m^2 (three exponentialHeightFogFactor): factor = 1 - exp(-(d*m)^2) ' +
  'with m = (top - worldY) * viewZ. At ground level under a 40 m top, d = 0.00025 reaches ~63% ' +
  'fog at 100 m. Fog vanishes entirely above the top.';

/** Fog above `topM` is exactly zero — the one behaviour that surprises an artist coming from linear. */
export const HEIGHT_TOP_NOTE =
  'Above "Fog top" there is NO fog at all (the factor is clamped at 0), which is the point of a ' +
  'height fog: valleys fill, towers stay clear. A camera above the top still sees fog on what it ' +
  'looks down INTO - the term is evaluated at the shaded surface, not at the eye.';

/**
 * ── ⭐ THE ONE FOLD ─────────────────────────────────────────────────────────────────────────────
 *
 * (config × tier) → what the frame runs and what the panel says. Total: every arm returns a plan,
 * `off` included, so no caller has to decide what "no fog" means for itself.
 */
export function resolveFogPlan(cfg: FogConfigInput, tier: Tier): FogPlan {
  const row = FOG_TIER_ROWS[tier];
  const base = {
    tier,
    authoredMode: cfg.mode,
    color: cfg.color,
    nearM: cfg.nearM,
    farM: cfg.farM,
    density: cfg.density,
    topM: cfg.topM,
  };
  if (!cfg.enabled) {
    return {
      ...base,
      impl: 'off',
      downgraded: false,
      notes: [
        'Fog is OFF: scene.fog and scene.fogNode are both null, so no material carries the term ' +
          'at all. This is the escape hatch the hard-coded Fog(0x1a2836, 60, 150) never had.',
      ],
    };
  }
  const notes: string[] = [row.note];
  /*
   * The downgrade arm. It cannot fire today — every tier's list holds both authored modes — and it
   * is written anyway, because the alternative is that the FIRST caller to meet a tier that cannot
   * run a mode discovers it as a black screen. `downgraded` is what `__fwg.state().fog` publishes
   * and what the panel chips, so a silent fallback is impossible by construction rather than by
   * convention. `impls[0]` is the tier's floor: `linear` is on every list because it is three's own
   * node and works on both backends.
   */
  const supported = row.impls.includes(cfg.mode);
  const impl: FogImpl = supported ? cfg.mode : (row.impls[0] ?? 'linear');
  if (!supported) {
    notes.push(
      `Tier ${tier} cannot run "${cfg.mode}" fog - running "${impl}" instead. The authored value ` +
        'is untouched; this is a per-device downgrade, not an edit to the level.',
    );
  }
  if (impl === 'height') notes.push(HEIGHT_DENSITY_NOTE, HEIGHT_TOP_NOTE);
  return { ...base, impl, downgraded: !supported, notes };
}

/** The header chip: `tier 2 · height`. One vocabulary for the panel, the state hook and the tests. */
export function describeFogPlan(plan: FogPlan): string {
  if (plan.impl === 'off') return `tier ${plan.tier} - fog off`;
  const range =
    plan.impl === 'height'
      ? `top ${plan.topM} m, density ${plan.density}`
      : `${plan.nearM}-${plan.farM} m`;
  return (
    `tier ${plan.tier} - ${plan.impl} - ${range}` +
    (plan.downgraded ? ` (authored ${plan.authoredMode}, downgraded)` : '')
  );
}

/**
 * ── ⭐ HOW WE WOULD KNOW IT STOPPED WORKING ─────────────────────────────────────────────────────
 *
 * `plan` is what the fold DECIDED; `sceneFog` is what the scene CARRIES, read off
 * `scene.fog`/`scene.fogNode` themselves. A silent fallback shows as the two disagreeing, and a
 * hitchy editor shows as `keyCycles` climbing during a drag that should be uniform writes only.
 * Published as `__fwg.state().fog`.
 */
export interface FogState {
  readonly plan: FogPlan;
  /** One-line summary — `describeFogPlan(plan)`, so the panel and the hook cannot word it apart. */
  readonly summary: string;
  /**
   * READ OFF THE SCENE, never off the plan (R1.6-D: a self-report proves nothing).
   * `none` = neither handle set · `linear` = a `THREE.Fog` and no node · `node` = `scene.fogNode`.
   */
  readonly sceneFog: 'none' | 'linear' | 'node';
  /** The height node's live uniform values, or null when no height node has ever been built. */
  readonly uniforms: { readonly color: string; readonly density: number; readonly topM: number } | null;
  /**
   * How many times the fog NODE was swapped this session. Each one regenerates every visible
   * material's WGSL (`NodeManager.getCacheKey` folds `fogNode.getCacheKey()` into the scene key),
   * so this is the fog analogue of `WorldLightRigInfo.keyMoves` — and a colour drag must add 0.
   */
  readonly keyCycles: number;
}

export interface FogFieldPlan {
  readonly shown: readonly string[];
  readonly hidden: readonly string[];
  readonly labels: Readonly<Record<string, string>>;
}

/**
 * Which of `WorldFogSchema`'s keys MEAN something in this mode — `lightFields`' rule, verbatim:
 * *HIDDEN, NOT WARNED*. A linear fog showing `density` and `topM` is two controls that change
 * nothing, and a fog panel is exactly where an artist would conclude the whole feature is broken.
 *
 * ⚠️ `shown ∪ hidden === Object.keys(WorldFogSchema.shape)` is asserted in
 * `tests/editorDetails.test.ts` for BOTH modes — the fence against the other direction, a key
 * silently dropped from every inspector.
 */
export function fogFields(mode: FogMode): FogFieldPlan {
  const labels: Record<string, string> = {
    enabled: 'Fog on',
    mode: 'Fog mode',
    color: 'Fog colour',
    nearM: 'Fog start (m from camera)',
    farM: 'Fog full (m from camera)',
    density: 'Height density (1/m^2)',
    topM: 'Fog top (m, world Y)',
  };
  const always = ['enabled', 'mode', 'color'];
  if (mode === 'height') {
    return { shown: [...always, 'density', 'topM'], hidden: ['nearM', 'farM'], labels };
  }
  return { shown: [...always, 'nearM', 'farM'], hidden: ['density', 'topM'], labels };
}
